export type AppSportContext = 'tennis' | 'padel';
export type AppAreaContext = 'public' | 'admin';

export interface IAppContext {
    area: AppAreaContext;
    sport: AppSportContext;
    isAdmin: boolean;
    isPublic: boolean;
    isPadel: boolean;
    isTennis: boolean;
    hostname: string;
    sportLabel: string;
}

const ADMIN_HOST_PREFIX = 'my';
const PADEL_HOST_PART = 'padel';
const DEFAULT_PUBLIC_ORIGIN = 'https://amatour.ru';

const SPORT_LABELS: Record<AppSportContext, string> = {
    padel: 'Падел',
    tennis: 'Теннис'
};

export const getHostname = () => {
    if (typeof window === 'undefined') {
        return '';
    }

    return window.location.hostname.toLowerCase();
};

export const getOppositeSport = (sport: AppSportContext): AppSportContext =>
    sport === 'padel' ? 'tennis' : 'padel';

export const getSportLabel = (sport: AppSportContext) => SPORT_LABELS[sport];

export const getAppContext = (hostname = getHostname()): IAppContext => {
    const hostParts = hostname.split('.').filter(Boolean);
    const area: AppAreaContext = hostParts[0] === ADMIN_HOST_PREFIX ? 'admin' : 'public';
    const sport: AppSportContext = hostParts.includes(PADEL_HOST_PART) ? 'padel' : 'tennis';

    return {
        area,
        sport,
        isAdmin: area === 'admin',
        isPublic: area === 'public',
        isPadel: sport === 'padel',
        isTennis: sport === 'tennis',
        hostname,
        sportLabel: getSportLabel(sport)
    };
};

export const getSportHostname = (hostname: string, targetSport: AppSportContext) => {
    const hostParts = hostname.toLowerCase().split('.').filter(Boolean);

    if (!hostParts.length) {
        return hostname;
    }

    if (targetSport === 'tennis') {
        return hostParts.filter((part) => part !== PADEL_HOST_PART).join('.');
    }

    if (hostParts.includes(PADEL_HOST_PART)) {
        return hostname;
    }

    const insertIndex = hostParts[0] === ADMIN_HOST_PREFIX ? 1 : 0;
    const nextHostParts = [...hostParts];
    nextHostParts.splice(insertIndex, 0, PADEL_HOST_PART);

    return nextHostParts.join('.');
};

const getPublicHostname = (hostname: string) =>
    hostname
        .toLowerCase()
        .split('.')
        .filter(Boolean)
        .filter((part, index) => !(index === 0 && part === ADMIN_HOST_PREFIX))
        .join('.');

const getOriginWithProtocol = (origin: string) => {
    if (!origin) {
        return DEFAULT_PUBLIC_ORIGIN;
    }

    return /^https?:\/\//.test(origin) ? origin : `https://${origin}`;
};

export const getPublicSportOrigin = (
    sport: AppSportContext = getAppContext().sport,
    origin = process.env.REACT_APP_HOST || process.env.REACT_APP_MEDIA_LINK || DEFAULT_PUBLIC_ORIGIN
) => {
    try {
        const url = new URL(getOriginWithProtocol(origin));
        url.hostname = getSportHostname(getPublicHostname(url.hostname), sport);
        url.pathname = '';
        url.search = '';
        url.hash = '';

        return url.toString().replace(/\/$/, '');
    } catch {
        return DEFAULT_PUBLIC_ORIGIN;
    }
};

export const getPublicTournamentUrl = (
    tournamentId: string | number,
    path = '',
    sport: AppSportContext = getAppContext().sport
) => `${getPublicSportOrigin(sport)}/tournament/${tournamentId}${path}`;

export const getPublicShortTournamentUrl = (
    tournamentId: string | number,
    sport: AppSportContext = getAppContext().sport
) => `${getPublicSportOrigin(sport)}/t/${tournamentId}`;

export const getPublicPlayerUrl = (
    playerId: string | number,
    sport: AppSportContext = getAppContext().sport
) => `${getPublicSportOrigin(sport)}/players/${playerId}`;

export const getPublicAssetUrl = (path: string, sport: AppSportContext = getAppContext().sport) =>
    `${getPublicSportOrigin(sport)}${path.startsWith('/') ? path : `/${path}`}`;

export const getPublicWebSocketUrl = (
    path: string,
    sport: AppSportContext = getAppContext().sport
) => {
    const origin = getPublicSportOrigin(sport);
    const url = new URL(`${origin}${path.startsWith('/') ? path : `/${path}`}`);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';

    return url.toString();
};

export const getSportSwitchUrl = (targetSport: AppSportContext) => {
    if (typeof window === 'undefined') {
        return '#';
    }

    const nextUrl = new URL(window.location.href);
    nextUrl.hostname = getSportHostname(nextUrl.hostname, targetSport);

    return nextUrl.toString();
};
